import React, { useEffect, useRef, useState } from 'react'
import classnames from 'classnames'

// Small shared form controls for the SABER read screens. Light/dark aware,
// following the slate-based conventions used by the rest of the admin app.

export interface SegmentedOption<T extends string> {
  value: T
  label: React.ReactNode
}

export interface SegmentedControlProps<T extends string> {
  options: Array<SegmentedOption<T>>
  value: T
  onChange: (value: T) => void
  ariaLabel?: string
  className?: string
}

const SEGMENTED_CONTAINER =
  'inline-flex overflow-hidden rounded-md border border-slate-200 dark:border-slate-700'

const segmentButtonClass = (active: boolean, disabled = false): string =>
  classnames(
    'px-3 py-1.5 text-xs font-medium transition-colors',
    disabled
      ? 'cursor-not-allowed bg-slate-50 text-slate-300 dark:bg-slate-800/60 dark:text-slate-600'
      : active
        ? 'bg-indigo-600 text-white dark:bg-indigo-500'
        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
  )

// A horizontal segmented (pill) button group — used for period / granularity toggles.
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className
}: SegmentedControlProps<T>) {
  return (
    <div role="group" aria-label={ariaLabel} className={classnames(SEGMENTED_CONTAINER, className)}>
      {options.map((opt) => {
        const active = opt.value === value
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
            className={segmentButtonClass(active)}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

export interface MultiSegmentedControlProps {
  options: readonly string[]
  isActive: (option: string) => boolean
  onToggle: (option: string) => void
  ariaLabel: string
  formatLabel?: (option: string) => string
  getTitle?: (option: string) => string | undefined
  /** When false, the segment is shown greyed out and cannot be toggled. */
  isAvailable?: (option: string) => boolean
  className?: string
}

/** SegmentedControl styling where each segment toggles independently (status/category filters). */
export const MultiSegmentedControl: React.FC<MultiSegmentedControlProps> = ({
  options,
  isActive,
  onToggle,
  ariaLabel,
  formatLabel,
  getTitle,
  isAvailable,
  className
}) => (
  <div role="group" aria-label={ariaLabel} className={classnames(SEGMENTED_CONTAINER, className)}>
    {options.map((opt) => {
      const available = isAvailable?.(opt) ?? true
      const active = available && isActive(opt)
      return (
        <button
          key={opt}
          type="button"
          aria-pressed={active}
          aria-disabled={!available}
          disabled={!available}
          title={getTitle?.(opt)}
          onClick={() => {
            if (available) onToggle(opt)
          }}
          className={segmentButtonClass(active, !available)}
        >
          {formatLabel != null ? formatLabel(opt) : opt}
        </button>
      )
    })}
  </div>
)

/** Inline label beside a table filter control (Status, Variance, Group by). */
export const FilterLabeledControl: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({
  label,
  children,
  className
}) => (
  <div className={classnames('flex items-center gap-2', className)}>
    <span className="shrink-0 text-xs font-medium text-slate-500 dark:text-slate-400">{label}</span>
    {children}
  </div>
)

export interface LabeledFieldProps {
  label: string
  htmlFor?: string
  children: React.ReactNode
  className?: string
}

// A small uppercase label above a control.
export const LabeledField: React.FC<LabeledFieldProps> = ({ label, htmlFor, children, className }) => (
  <div className={classnames('flex flex-col gap-1', className)}>
    <label
      htmlFor={htmlFor}
      className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
    >
      {label}
    </label>
    {children}
  </div>
)

const SELECT_CLASS =
  'rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 ' +
  'text-sm text-slate-900 dark:text-slate-100 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500'

export interface SelectControlProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  className?: string
}

// A styled <select>, light/dark aware.
export const SelectControl: React.FC<SelectControlProps> = ({ className, children, ...rest }) => (
  <select className={classnames(SELECT_CLASS, className)} {...rest}>
    {children}
  </select>
)

export interface MonthInputProps {
  id?: string
  value: string
  onChange: (value: string) => void
  className?: string
}

// A native month picker bound to a YYYY-MM-DD first-of-month string.
export const MonthInput: React.FC<MonthInputProps> = ({ id, value, onChange, className }) => (
  <input
    id={id}
    type="month"
    // value prop is YYYY-MM-DD; the month input wants YYYY-MM.
    value={value.slice(0, 7)}
    onChange={(e) => {
      const ym = e.target.value
      onChange(ym ? `${ym}-01` : '')
    }}
    className={classnames(SELECT_CLASS, className)}
  />
)

export interface CheckboxControlProps {
  id?: string
  checked: boolean
  onChange: (checked: boolean) => void
  label: React.ReactNode
  className?: string
}

// A labeled checkbox, light/dark aware.
export const CheckboxControl: React.FC<CheckboxControlProps> = ({ id, checked, onChange, label, className }) => (
  <label htmlFor={id} className={classnames('inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200', className)}>
    <input
      id={id}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500"
    />
    {label}
  </label>
)

export interface MultiSelectControlProps {
  id?: string
  /** All selectable option values. */
  options: string[]
  /** Currently selected values. Empty selection means "none selected". */
  selected: string[]
  onChange: (selected: string[]) => void
  /** Label shown on the trigger button (inline mode only). */
  label: string
  className?: string
  /** Compact sizing (text-xs, tighter padding) to match chip-style bars (WEB-6581). */
  compact?: boolean
  /** Stacked filter-field mode — omits the `label:` prefix on the trigger. */
  field?: boolean
  /** Trigger text when every option is selected (field mode), e.g. "All utilities". */
  allSummary?: string
  /** Trigger text when nothing is selected (field mode). */
  noneSummary?: string
}

// Prototype-style uppercase label above a filter control in the combined bar.
export const FilterField: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({
  label,
  children,
  className
}) => (
  <div className={classnames('flex min-w-[8.5rem] flex-col gap-1', className)}>
    <span className="text-[10px] uppercase font-normal tracking-wider text-slate-500 dark:text-slate-400">{label}</span>
    {children}
  </div>
)

export const FIELD_CONTROL_TRIGGER =
  'flex w-full min-w-[8.5rem] items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-left text-xs font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100'

// A multi-select dropdown of checkboxes (#1 Utility filter). Closes on outside
// click. Light/dark aware, matching the slate-based control conventions.
export const MultiSelectControl: React.FC<MultiSelectControlProps> = ({
  id,
  options,
  selected,
  onChange,
  label,
  className,
  compact = false,
  field = false,
  allSummary,
  noneSummary
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent): void => {
      if (ref.current != null && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const toggle = (value: string): void => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value])
  }

  const summary =
    selected.length === 0
      ? (noneSummary ?? 'None')
      : selected.length === options.length
        ? (allSummary ?? 'All')
        : selected.length === 1
          ? selected[0]
          : `${selected.length} selected`

  const triggerText = field ? summary : `${label}: ${summary}`

  return (
    <div ref={ref} className={classnames('relative', className)}>
      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={classnames(
          field
            ? FIELD_CONTROL_TRIGGER
            : 'flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white text-left text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100',
          !field && (compact ? 'min-w-[8rem] px-2.5 py-1.5 text-xs' : 'min-w-[10rem] px-3 py-1.5 text-sm')
        )}
      >
        <span>{triggerText}</span>
        <span aria-hidden className="text-[0.65rem] leading-none text-slate-400 dark:text-slate-500">
          ▾
        </span>
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable
          className="absolute z-20 mt-1 max-h-60 w-full min-w-[10rem] overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800"
        >
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400 dark:text-slate-500">No options</div>
          ) : (
            options.map((opt) => (
              <label
                key={opt}
                className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={() => toggle(opt)}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600"
                />
                {opt}
              </label>
            ))
          )}
        </div>
      )}
    </div>
  )
}
